import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import s from "./ConfirmDialog.module.css";

/**
 * Replacement for `window.confirm` with full styling, focus management,
 * and a destructive-tone variant. Cancel is the default focus target so
 * an accidental Enter never destroys data.
 *
 * Props:
 *   open          boolean
 *   title         string  (required)
 *   message       string  (optional, supporting copy)
 *   description   string  (optional, callout below message — e.g. the row name)
 *   confirmLabel  string  (default "Confirm")
 *   cancelLabel   string  (default "Cancel")
 *   tone          "default" | "danger"
 *   busy          boolean — disables both buttons and shows "Working…" on confirm
 *   onConfirm     fn()
 *   onCancel      fn()
 */
export default function ConfirmDialog({
  open,
  title,
  message,
  description,
  confirmLabel = "Confirm",
  cancelLabel  = "Cancel",
  tone         = "default",
  busy         = false,
  onConfirm,
  onCancel,
}) {
  const cancelRef  = useRef(null);
  const confirmRef = useRef(null);

  useEffect(() => {
    if (!open) return;

    function onKey(e) {
      if (e.key === "Escape") {
        e.preventDefault();
        if (!busy) onCancel?.();
        return;
      }
      // Lightweight focus trap between Cancel and Confirm so Tab cycles
      // within the dialog rather than wandering into the page behind.
      if (e.key === "Tab") {
        const els = [cancelRef.current, confirmRef.current].filter(Boolean);
        if (els.length < 2) return;
        const [first, last] = els;
        const active = document.activeElement;
        if (e.shiftKey && active === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
      }
    }
    document.addEventListener("keydown", onKey);

    // Default focus = Cancel. Destructive-default-safe: pressing Enter
    // immediately after the dialog appears will cancel, not confirm.
    const focusT = setTimeout(() => cancelRef.current?.focus(), 0);

    // Lock background scroll while the dialog is open.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKey);
      clearTimeout(focusT);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, busy, onCancel]);

  if (!open) return null;

  const danger = tone === "danger";

  return createPortal(
    <div
      className={s.backdrop}>
      <div
        className={s.dialog}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="cd-title"
        aria-describedby={message ? "cd-msg" : undefined}
        onClick={(e) => e.stopPropagation()}>
        {danger && (
          <div className={s.iconWrap} aria-hidden="true">
            <span className={s.icon}>!</span>
          </div>
        )}
        <h3 id="cd-title" className={s.title}>{title}</h3>
        {message && <p id="cd-msg" className={s.message}>{message}</p>}
        {description && <div className={s.desc}>{description}</div>}
        <div className={s.actions}>
          <button
            ref={cancelRef}
            type="button"
            className={s.btnCancel}
            onClick={onCancel}
            disabled={busy}>
            {cancelLabel}
          </button>
          <button
            ref={confirmRef}
            type="button"
            className={danger ? s.btnDanger : s.btnPrimary}
            onClick={onConfirm}
            disabled={busy}>
            {busy ? "Working…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
